/**
 * deformReg.js — деформируемое совмещение двух объёмов (демоны Тириона,
 * многоуровневая пирамида). Чистая математика без импортов; проверяется в
 * node на фантоме с ЗАДАННЫМ полем деформации (tests/deform_test.mjs).
 *
 * ЗАЧЕМ. Слова владельца: «для точного совмещения нужна деформируемая
 * регистрация — пациент дышит и лежит иначе». Жёсткое совмещение (6 степеней,
 * rigidRegister.js) снимает укладку как целое; остаток — дыхание, наполнение
 * органов, изгиб позвоночника — жёстким не берётся по построению.
 *
 * МЕТОД. Демоны (Thirion 1998): итеративно двигаем воксели подвижного тома
 * силой, выведенной из оптического потока:
 *
 *     du = (f − m∘(id+u)) · ∇f / (|∇f|² + (f − m∘(id+u))²)
 *
 * где f — опорный том, m — подвижный, u — накопленное поле смещений. После
 * каждого шага поле сглаживается гауссом (диффузионные демоны) — это и есть
 * регуляризация: соседние воксели не разъезжаются врозь. Считается пирамидой
 * от грубого уровня к мелкому: крупные смещения ловятся там, где они дёшевы.
 *
 * ЧЕСТНОСТЬ НАЗНАЧЕНИЯ. Поле предназначено для НАВИГАЦИИ и переноса точек
 * (синхронная прокрутка по анатомии, соответствие очагов между датами) — НЕ
 * для деформирования показываемых изображений и НЕ для измерений по
 * деформированной картинке: искривлённый снимок — это уже не снимок пациента.
 * Об этом сказано и в окне, которое этим модулем пользуется.
 *
 * ЕДИНИЦЫ. Модуль работает в ВОКСЕЛЯХ переданной сетки; входные тома обязаны
 * лежать НА ОДНОЙ сетке (обвязка ресэмплит подвижный на сетку опорного, уже
 * применив жёсткое совмещение). Перевод в мм и в мировые координаты — забота
 * обвязки, у которой есть spacing/origin/direction.
 */

/** Трилинейная выборка; вне тома — NaN (обвязка решает, что с краем). */
export function трилин(данные, dims, x, y, z) {
  const [nx, ny, nz] = dims;
  if (!(x >= 0 && y >= 0 && z >= 0 && x <= nx - 1 && y <= ny - 1 && z <= nz - 1)) return NaN;
  const x0 = Math.floor(x), y0 = Math.floor(y), z0 = Math.floor(z);
  const x1 = Math.min(x0 + 1, nx - 1), y1 = Math.min(y0 + 1, ny - 1), z1 = Math.min(z0 + 1, nz - 1);
  const fx = x - x0, fy = y - y0, fz = z - z0;
  const i = (X, Y, Z) => данные[X + Y * nx + Z * nx * ny];
  const c00 = i(x0, y0, z0) * (1 - fx) + i(x1, y0, z0) * fx;
  const c10 = i(x0, y1, z0) * (1 - fx) + i(x1, y1, z0) * fx;
  const c01 = i(x0, y0, z1) * (1 - fx) + i(x1, y0, z1) * fx;
  const c11 = i(x0, y1, z1) * (1 - fx) + i(x1, y1, z1) * fx;
  const c0 = c00 * (1 - fy) + c10 * fy;
  const c1 = c01 * (1 - fy) + c11 * fy;
  return c0 * (1 - fz) + c1 * fz;
}

/** Уменьшение вдвое усреднением 2×2×2 (последний нечётный слой усредняется тем, что есть). */
export function уменьшитьВдвое(данные, dims) {
  const [nx, ny, nz] = dims;
  const mx = Math.max(1, Math.floor(nx / 2)), my = Math.max(1, Math.floor(ny / 2)), mz = Math.max(1, Math.floor(nz / 2));
  const вых = new Float32Array(mx * my * mz);
  for (let z = 0; z < mz; z++) for (let y = 0; y < my; y++) for (let x = 0; x < mx; x++) {
    let с = 0, к = 0;
    for (let dz = 0; dz < 2; dz++) for (let dy = 0; dy < 2; dy++) for (let dx = 0; dx < 2; dx++) {
      const X = 2 * x + dx, Y = 2 * y + dy, Z = 2 * z + dz;
      if (X >= nx || Y >= ny || Z >= nz) continue;
      с += данные[X + Y * nx + Z * nx * ny]; к++;
    }
    вых[x + y * mx + z * mx * my] = с / к;
  }
  return { данные: вых, dims: [mx, my, mz] };
}

/** Сепарабельное гауссово сглаживание НА МЕСТЕ (сигма в вокселях; ядро ±3σ). */
export function сгладить(поле, dims, сигма) {
  if (!(сигма > 0)) return поле;
  const [nx, ny, nz] = dims;
  const r = Math.max(1, Math.round(3 * сигма));
  const ядро = new Float64Array(2 * r + 1);
  let сум = 0;
  for (let k = -r; k <= r; k++) { ядро[k + r] = Math.exp(-(k * k) / (2 * сигма * сигма)); сум += ядро[k + r]; }
  for (let k = 0; k < ядро.length; k++) ядро[k] /= сум;
  const буф = new Float32Array(поле.length);
  const оси = [[1, nx, ny * nz, (a, b) => a + 0 * b], null, null]; // не используется — ниже явные три прохода
  void оси;
  // ось X
  for (let z = 0; z < nz; z++) for (let y = 0; y < ny; y++) {
    const база = y * nx + z * nx * ny;
    for (let x = 0; x < nx; x++) {
      let с = 0;
      for (let k = -r; k <= r; k++) {
        const xx = Math.min(nx - 1, Math.max(0, x + k));
        с += поле[база + xx] * ядро[k + r];
      }
      буф[база + x] = с;
    }
  }
  поле.set(буф);
  // ось Y
  for (let z = 0; z < nz; z++) for (let x = 0; x < nx; x++) {
    const база = x + z * nx * ny;
    for (let y = 0; y < ny; y++) {
      let с = 0;
      for (let k = -r; k <= r; k++) {
        const yy = Math.min(ny - 1, Math.max(0, y + k));
        с += поле[база + yy * nx] * ядро[k + r];
      }
      буф[база + y * nx] = с;
    }
  }
  поле.set(буф);
  // ось Z
  for (let y = 0; y < ny; y++) for (let x = 0; x < nx; x++) {
    const база = x + y * nx;
    for (let z = 0; z < nz; z++) {
      let с = 0;
      for (let k = -r; k <= r; k++) {
        const zz = Math.min(nz - 1, Math.max(0, z + k));
        с += поле[база + zz * nx * ny] * ядро[k + r];
      }
      буф[база + z * nx * ny] = с;
    }
  }
  поле.set(буф);
  return поле;
}

/** Увеличить поле вдвое (до dimsБол) трилинейной интерполяцией, значения ×2. */
export function увеличитьПолеВдвое(u, dimsМал, dimsБол) {
  const [nx, ny, nz] = dimsБол;
  const вых = new Float32Array(nx * ny * nz);
  const kx = (dimsМал[0] - 1) / Math.max(1, nx - 1);
  const ky = (dimsМал[1] - 1) / Math.max(1, ny - 1);
  const kz = (dimsМал[2] - 1) / Math.max(1, nz - 1);
  for (let z = 0; z < nz; z++) for (let y = 0; y < ny; y++) for (let x = 0; x < nx; x++) {
    const v = трилин(u, dimsМал, x * kx, y * ky, z * kz);
    вых[x + y * nx + z * nx * ny] = (Number.isNaN(v) ? 0 : v) * 2;
  }
  return вых;
}

function _mse(f, m, dims, ux, uy, uz) {
  const [nx, ny, nz] = dims;
  let с = 0, к = 0;
  for (let z = 0; z < nz; z++) for (let y = 0; y < ny; y++) for (let x = 0; x < nx; x++) {
    const i = x + y * nx + z * nx * ny;
    const v = трилин(m, dims, x + (ux ? ux[i] : 0), y + (uy ? uy[i] : 0), z + (uz ? uz[i] : 0));
    if (Number.isNaN(v)) continue;
    const d = v - f[i];
    с += d * d; к++;
  }
  return к ? с / к : NaN;
}

/** Один уровень демонов. Поля ux/uy/uz правятся на месте. */
function _демоныУровень(f, m, dims, ux, uy, uz, { итераций, сигмаОбнов, шагМакс }) {
  const [nx, ny, nz] = dims;
  const eps = 1e-9;
  for (let ит = 0; ит < итераций; ит++) {
    const dux = new Float32Array(f.length);
    const duy = new Float32Array(f.length);
    const duz = new Float32Array(f.length);
    for (let z = 1; z < nz - 1; z++) for (let y = 1; y < ny - 1; y++) for (let x = 1; x < nx - 1; x++) {
      const i = x + y * nx + z * nx * ny;
      const mv = трилин(m, dims, x + ux[i], y + uy[i], z + uz[i]);
      if (Number.isNaN(mv)) continue;
      const diff = f[i] - mv;
      // Градиент ОПОРНОГО тома — центральные разности.
      const gx = (f[i + 1] - f[i - 1]) / 2;
      const gy = (f[i + nx] - f[i - nx]) / 2;
      const gz = (f[i + nx * ny] - f[i - nx * ny]) / 2;
      const зн = gx * gx + gy * gy + gz * gz + diff * diff + eps;
      let sx = (diff * gx) / зн, sy = (diff * gy) / зн, sz = (diff * gz) / зн;
      const дл = Math.hypot(sx, sy, sz);
      if (дл > шагМакс) { const k = шагМакс / дл; sx *= k; sy *= k; sz *= k; }
      dux[i] = sx; duy[i] = sy; duz[i] = sz;
    }
    // Диффузионная регуляризация: сглаживаем ПОЛЕ после прибавки шага.
    for (let i = 0; i < f.length; i++) { ux[i] += dux[i]; uy[i] += duy[i]; uz[i] += duz[i]; }
    сгладить(ux, dims, сигмаОбнов); сгладить(uy, dims, сигмаОбнов); сгладить(uz, dims, сигмаОбнов);
  }
}

/**
 * Демоны с пирамидой. Тома одной сетки; смещения — в ВОКСЕЛЯХ этой сетки.
 *
 * @param {{данные:Float32Array, dims:number[]}} fixed
 * @param {{данные:Float32Array, dims:number[]}} moving — уже на сетке fixed
 * @param {object} [o] — { уровней=3, итераций=[40,30,20] (от грубого к мелкому),
 *                         сигмаОбнов=1.0, шагМакс=0.7, сигмаИтог=1.0 }
 * @returns {{ok, ux,uy,uz:Float32Array, dims, mse0, mse1, улучшение, причина?}}
 */
export function демоны3д(fixed, moving, o = {}) {
  const f0 = fixed?.данные, m0 = moving?.данные, dims0 = fixed?.dims;
  if (!f0?.length || !m0?.length || f0.length !== m0.length) return { ok: false, причина: 'тома пусты или разных размеров' };
  const [nx, ny, nz] = dims0 || [];
  if (nx * ny * nz !== f0.length) return { ok: false, причина: 'размеры не сходятся с массивом' };
  if (Math.min(nx, ny, nz) < 8) return { ok: false, причина: `сетка ${nx}×${ny}×${nz} — для деформации слишком мелкая (нужно ≥ 8 по каждой оси)` };

  const уровней = Math.max(1, Math.min(4, o.уровней ?? 3));
  const итерПлан = o.итераций ?? [40, 30, 20, 15];
  const сигмаОбнов = o.сигмаОбнов ?? 1.0;
  const шагМакс = o.шагМакс ?? 0.7;

  // Пирамида: [0] — исходный уровень, дальше — вдвое грубее.
  const пирF = [{ данные: f0, dims: dims0 }], пирM = [{ данные: m0, dims: dims0 }];
  for (let l = 1; l < уровней; l++) {
    const пф = уменьшитьВдвое(пирF[l - 1].данные, пирF[l - 1].dims);
    if (Math.min(...пф.dims) < 8) break;
    пирF.push(пф);
    пирM.push(уменьшитьВдвое(пирM[l - 1].данные, пирM[l - 1].dims));
  }

  const mse0 = _mse(f0, m0, dims0, null, null, null);

  // От самого грубого к самому мелкому.
  let ux = null, uy = null, uz = null, dimsТек = null;
  for (let l = пирF.length - 1; l >= 0; l--) {
    const F = пирF[l], M = пирM[l];
    if (!ux) {
      ux = new Float32Array(F.данные.length);
      uy = new Float32Array(F.данные.length);
      uz = new Float32Array(F.данные.length);
    } else {
      ux = увеличитьПолеВдвое(ux, dimsТек, F.dims);
      uy = увеличитьПолеВдвое(uy, dimsТек, F.dims);
      uz = увеличитьПолеВдвое(uz, dimsТек, F.dims);
    }
    dimsТек = F.dims;
    const итераций = итерПлан[Math.min(пирF.length - 1 - l, итерПлан.length - 1)];
    _демоныУровень(F.данные, M.данные, F.dims, ux, uy, uz, { итераций, сигмаОбнов, шагМакс });
  }
  if (o.сигмаИтог > 0) { сгладить(ux, dims0, o.сигмаИтог); сгладить(uy, dims0, o.сигмаИтог); сгладить(uz, dims0, o.сигмаИтог); }

  const mse1 = _mse(f0, m0, dims0, ux, uy, uz);
  return { ok: true, ux, uy, uz, dims: dims0, mse0, mse1, улучшение: mse0 > 0 ? 1 - mse1 / mse0 : 0 };
}

/** Точка сетки ОПОРНОГО → соответствующая точка сетки ПОДВИЖНОГО: x + u(x). Вне поля — как есть. */
export function точкаПоПолю(поле, idx) {
  const { ux, uy, uz, dims } = поле;
  const [x, y, z] = idx;
  const dx = трилин(ux, dims, x, y, z), dy = трилин(uy, dims, x, y, z), dz = трилин(uz, dims, x, y, z);
  if (Number.isNaN(dx)) return [x, y, z];
  return [x + dx, y + dy, z + dz];
}

/**
 * Обратный перенос: найти x, для которого x + u(x) = y (итерация неподвижной
 * точки, 10 шагов — для гладких полей навигационной величины сходится быстро).
 */
export function точкаПоПолюНазад(поле, idxПодвижного) {
  const [yx, yy, yz] = idxПодвижного;
  let x = yx, y = yy, z = yz;
  for (let i = 0; i < 10; i++) {
    const dx = трилин(поле.ux, поле.dims, x, y, z);
    if (Number.isNaN(dx)) break;
    const dy = трилин(поле.uy, поле.dims, x, y, z);
    const dz = трилин(поле.uz, поле.dims, x, y, z);
    x = yx - dx; y = yy - dy; z = yz - dz;
  }
  return [x, y, z];
}

/** Сводка поля в мм — для честной подписи в окне. */
export function сводкаПоля(поле, spacing) {
  const { ux, uy, uz } = поле;
  const [sx, sy, sz] = spacing || [1, 1, 1];
  let сум = 0, макс = 0, n = 0;
  for (let i = 0; i < ux.length; i++) {
    const мм = Math.hypot(ux[i] * sx, uy[i] * sy, uz[i] * sz);
    сум += мм; n++;
    if (мм > макс) макс = мм;
  }
  return { среднееМм: n ? сум / n : 0, максМм: макс };
}
